import React, { useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

import { Card, CardContent, Grid, Typography, Button } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';

import { COLOR_E, COLOR_TOOLTIP_BACKGROUND, COLOR_PRIMARY_DARK } from 'src/const';
import { convertCurrency } from 'src/utils/functions';
import { BarChartData, UtilityData } from 'src/interfaces/idashboard';
import { HorizontalBarProps } from 'src/interfaces/iproptypes';
import { COLOR_GREY_600, COLOR_PRIMARY_2 } from 'src/colors';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import ShowMoreButton from '../../Components/Buttons/ShowMoreButton';
import { logAmplitudeEvent } from 'src/utils/analytics';

const useStyles = makeStyles((theme: Theme) => ({
  iconButton: {
    padding: 5,
  },
  horizontalBarHeaderText: {
    fontSize: 17,
    fontWeight: 400,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 100,
  },
  sampleText: {
    color: COLOR_E,
    fontWeight: 600,
    transform: 'rotate(-30deg)',
    msTransform: 'rotate(-30deg)' /* IE 9 */,
    webkitTransform: 'rotate(-30deg),' /* Opera, Chrome, and Safari */,
  },
  linearProgress: {
    width: '100%',
    marginBottom: 20,
    color: COLOR_PRIMARY_DARK,
  },
}));

const parseData = (data: UtilityData, currency: string | undefined): BarChartData[] => {
  let barData: BarChartData[] = [];
  for (let key in data) {
    let keyPretty: string = key;
    if (key == 'cleaningFee') {
      keyPretty = 'Cleaning Fee';
    }
    if (key == 'additionalPersonFee') {
      keyPretty = 'Additional Guest Fee';
    }
    barData.push({
      name: keyPretty,
      val: currency ? convertCurrency(currency, data[key]) : data[key],
    });
  }
  return barData;
};

const getTootltipKey = (title: string, label: string) => {
  if (title == 'Extra Pricing') {
    return 'Price';
  }
  return label;
};

const HorizontalBarCard = ({
  title,
  data,
  color,
  unit,
  isDisabled,
  currency,
  handleOnSampleClick,
  graphLimit,
  onCTAClick,
  matches,
  userTailorMarketAccess,
  tooltipSuffix,
  parent,
  warningTextComponent,
}: HorizontalBarProps) => {
  const classes = useStyles();
  const upgradeMessage = useUpgradeMesageProFeature();

  let graphData: BarChartData[] = parseData(data, currency);
  let noPropertyAvail: boolean = false;
  if (graphData.length === 0) {
    noPropertyAvail = true;
  }

  if (graphLimit) {
    graphData = graphData.slice(0, graphLimit);
  }

  // let chartContainerHeight: number = graphData.length > 5 ? 400 : 200;
  let chartContainerHeight: number = (graphData.length * 35);
  let barSize: number = 9;

  let blurGraph = {};
  // if (isDisabled) {
  //   blurGraph = {
  //     webkitFilter: 'blur(2px)',
  //   };
  // }

  const CustomTooltip = ({ active, payload, label, classes, tooltipSuffix }) => {
    useEffect(() => {
      if (active && parent) {
        logAmplitudeEvent('tooltip_interacted', {
          tooltip_type: 'graph',
          parent: parent,
        });
      }
    }, [active]);

    if (active) {
      return (
        <Card style={{ boxShadow: `0px 0px 10px 0px ${COLOR_GREY_600}` }}>
          <CardContent style={{ background: '#fff' }}>
            <Typography
              variant="body2"
              style={{ fontWeight: 500, color: COLOR_GREY_600, maxWidth: '180px' }}
            >
              {getTootltipKey(title, label)}:{' '}
              <span style={{ color: color }}>
                {payload[0].value}
                {payload[0].payload.name === 'Additional Guest Fee' ? `${unit}/pp` : unit}
                {tooltipSuffix && tooltipSuffix}
              </span>
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  return (
    <Card style={{ flex: 1 }}>
      <CardContent>
        {warningTextComponent && warningTextComponent}
        <Grid container>
          <Typography
            variant="body1"
            className="subHeadings"
            style={{
              marginBottom: '12px',
              height: window ? (window.innerWidth <= 1138 ? '50px' : '30px') : '50px',
            }}
          >
            {title}
          </Typography>

          <Grid container item xs={12} style={{ position: 'relative', top: '0', left: '0' }}>
            <ResponsiveContainer height={chartContainerHeight} width="100%">
              {noPropertyAvail ? (
                <Typography color="textSecondary">No property matching this criteria. </Typography>
              ) : (
                <BarChart
                  width={400}
                  height={chartContainerHeight}
                  data={graphData}
                  layout="vertical"
                  margin={{
                    top: 3,
                    right: 30,
                    left: 20,
                    bottom: 3,
                  }}
                  barCategoryGap={9}
                  style={blurGraph}
                >
                  <XAxis type="number" hide={true} />

                  <YAxis
                    style={{
                      fontSize: '0.75rem',
                      fontFamily: 'Roboto',
                    }}
                    domain={[0, 5000]}
                    dataKey="name"
                    type="category"
                  />

                  <Tooltip
                    contentStyle={{
                      fontSize: '0.85rem',
                      fontFamily: 'Roboto',
                    }}
                    cursor={{ fill: 'transparent' }}
                    content={
                      <CustomTooltip
                        classes={classes}
                        active
                        payload
                        label
                        tooltipSuffix={tooltipSuffix}
                      />
                    }
                  />

                  <Bar
                    isAnimationActive={true}
                    animationDuration={250}
                    dataKey="val"
                    unit={unit}
                    fill={color}
                    background={{ fill: '#eee' }}
                    barSize={barSize}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
            {isDisabled && (
              <div
                style={{
                  height: '100%',
                  position: 'absolute',
                  top: '0',
                  left: '80px',
                  width: '60%',
                  background: '#ffffff',
                  filter: 'blur( 6px)',
                  opacity: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              ></div>
            )}
            {isDisabled && handleOnSampleClick && (
              <Typography
                style={{
                  position: 'absolute',
                  top: '40%',
                  left: matches ? '50%' : '60%',
                  fontSize: '20px',
                  fontWeight: 500,
                  color: COLOR_PRIMARY_2,
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  textDecoration: 'underline',
                  transform: 'translate(-50%, -50%)',
                }}
                onClick={() => {
                  handleOnSampleClick('expert');
                }}
              >
                {upgradeMessage}
              </Typography>
            )}
          </Grid>

          {graphLimit && (
            <ShowMoreButton
              isShowingLess={graphLimit !== -1}
              handleButtonClick={onCTAClick ? onCTAClick : () => {}}
            />
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default HorizontalBarCard;
